import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle2, ArrowRight } from 'lucide-react';

const PaymentSuccess = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [paymentDetails, setPaymentDetails] = useState(null);

  useEffect(() => {
    // Get payment details from session storage
    const studentEmail = sessionStorage.getItem('student_email');
    const paymentType = sessionStorage.getItem('payment_type');
    const amount = sessionStorage.getItem('payment_amount');
    
    if (studentEmail) {
      setPaymentDetails({
        email: studentEmail,
        type: paymentType,
        amount: amount
      });
    }

    // Clear session storage after 5 seconds
    const timeout = setTimeout(() => {
      sessionStorage.removeItem('student_email');
      sessionStorage.removeItem('payment_type');
      sessionStorage.removeItem('payment_amount');
    }, 5000);

    return () => clearTimeout(timeout);
  }, []);

  const handleContinue = () => {
    if (paymentDetails?.email) {
      navigate('/student-login');
    } else {
      navigate('/');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-blue-50 flex items-center justify-center p-4">
      <div className="max-w-2xl w-full">
        <div className="bg-white rounded-2xl shadow-2xl p-8 md:p-12">
          {/* Success Icon */}
          <div className="flex justify-center mb-6">
            <div className="bg-emerald-100 rounded-full p-4">
              <CheckCircle2 className="w-16 h-16 text-emerald-600" />
            </div>
          </div>

          {/* Success Message */}
          <div className="text-center mb-8">
            <h1 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Payment Successful!
            </h1>
            <p className="text-lg text-slate-600">
              Thank you for your payment. Your transaction has been completed successfully.
            </p>
          </div>

          {/* Payment Details */}
          {paymentDetails && (
            <div className="bg-slate-50 rounded-xl p-6 mb-8">
              <h2 className="font-semibold text-slate-900 mb-4">Payment Details</h2>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-slate-600">Email:</span>
                  <span className="font-medium text-slate-900">{paymentDetails.email}</span>
                </div>
                {paymentDetails.type && (
                  <div className="flex justify-between">
                    <span className="text-slate-600">Payment Type:</span>
                    <span className="font-medium text-slate-900 capitalize">{paymentDetails.type}</span>
                  </div>
                )}
                {paymentDetails.amount && (
                  <div className="flex justify-between">
                    <span className="text-slate-600">Amount:</span>
                    <span className="font-medium text-slate-900">£{paymentDetails.amount}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Next Steps */}
          <div className="bg-blue-50 rounded-xl p-6 mb-8">
            <h2 className="font-semibold text-slate-900 mb-3">What's Next?</h2>
            <ul className="space-y-2 text-slate-700">
              <li className="flex items-start">
                <CheckCircle2 className="w-5 h-5 text-blue-600 mr-2 mt-0.5 flex-shrink-0" />
                <span>You will receive a confirmation email shortly</span>
              </li>
              <li className="flex items-start">
                <CheckCircle2 className="w-5 h-5 text-blue-600 mr-2 mt-0.5 flex-shrink-0" />
                <span>Access your student dashboard to track your progress</span>
              </li>
              <li className="flex items-start">
                <CheckCircle2 className="w-5 h-5 text-blue-600 mr-2 mt-0.5 flex-shrink-0" />
                <span>Course materials will be available on your selected start date</span>
              </li>
            </ul>
          </div>

          {/* Action Buttons */}
          <div className="space-y-3">
            <button
              onClick={handleContinue}
              className="w-full bg-emerald-600 text-white py-4 rounded-xl font-semibold hover:bg-emerald-700 transition-colors flex items-center justify-center group"
            >
              Go to Student Dashboard
              <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </button>
            
            <button
              onClick={() => navigate('/')}
              className="w-full bg-slate-100 text-slate-700 py-4 rounded-xl font-semibold hover:bg-slate-200 transition-colors"
            >
              Return to Home
            </button>
          </div>

          {/* Support Link */}
          <div className="text-center mt-8 text-sm text-slate-500">
            Need help? <a href="/support" className="text-emerald-600 hover:text-emerald-700 font-medium">Contact Support</a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentSuccess;
